import React, { useState, useEffect } from 'react';
import {
    Button, Card, CardContent, CardHeader, Box, Typography, Link
} from '@mui/material';
import authService from '../../api-authorization/AuthorizeService';
import { useToast } from '../../../hooks/useToast';
import { ToastNotification } from '../../common/ToastNotification';
import { formatApiError } from '../../../utils/errorFormatter';

export const UploadRefunds = () => {
    const [file, setFile] = useState<File | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);

    const { toast, showError, showSuccess, showWarning, handleClose } = useToast();

    useEffect(() => {
        const checkAdminRole = async () => {
            const user = await authService.getUser();
            const roles = user?.profile?.role;
            if (roles && (Array.isArray(roles) ? roles.includes('Admin') : roles === 'Admin')) {
                setIsAdmin(true);
            }
        };
        checkAdminRole();
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            showWarning('Пожалуйста, выберите файл для загрузки');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const text = e.target?.result;
            if (typeof text === 'string') {
                const lines = text.split('\n').filter(line => line.trim() !== '');
                let hasError = false;

                // Пропускаем первую строку (заголовки)
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i];
                    const index = i;

                    if (hasError) break;

                    const [terminalId, info, refundText, dateTime, sum, externalId] = line.split(',');

                    // Номер строки для отображения (с учетом заголовка, нумерация с 1)
                    const lineNumber = index + 1;

                    if (!externalId || !externalId.trim()) {
                        showError(`Строка ${lineNumber}: отсутствует обязательное поле ExternalId`);
                        hasError = true;
                        continue;
                    }

                    if (!terminalId || isNaN(parseInt(terminalId))) {
                        showError(`Строка ${lineNumber}: неверное значение TerminalId "${terminalId}"`);
                        hasError = true;
                        continue;
                    }

                    if (!sum || isNaN(parseFloat(sum))) {
                        showError(`Строка ${lineNumber}: неверное значение Sum "${sum}"`);
                        hasError = true;
                        continue;
                    }

                    // Валидация и парсинг даты
                    const parsedDate = new Date(dateTime);
                    if (isNaN(parsedDate.getTime())) {
                        showError(`Строка ${lineNumber}: неверный формат даты "${dateTime}"\n\nИспользуйте формат ISO: 2024-01-15T14:30:00`);
                        hasError = true;
                        continue;
                    }

                    const refund = {
                        terminalId: parseInt(terminalId),
                        info: info || '',
                        text: refundText || '',
                        dateTime: parsedDate.toISOString(),
                        sum: parseFloat(sum),
                        externalId: externalId.trim()
                    };

                    try {
                        const response = await fetch('api/refunds/upload', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(refund),
                        });

                        if (!response.ok) {
                            const errorMsg = await formatApiError(response, `Ошибка загрузки строки ${lineNumber}`);
                            showError(errorMsg);
                            hasError = true;
                        }
                    } catch (error: any) {
                        showError(`Сетевая ошибка при обработке строки ${lineNumber}: ${error.message || 'Неизвестная ошибка'}`);
                        hasError = true;
                    }
                }

                if (!hasError) {
                    showSuccess(`Загрузка успешно завершена. Обработано строк: ${lines.length - 1}`);
                } else {
                    showError('В процессе загрузки возникли ошибки. Проверьте данные и попробуйте снова');
                }
            }
        };
        reader.readAsText(file);
    };

    const handleDownloadTemplate = () => {
        const headers = "TerminalId,Info,Text,DateTime,Sum,ExternalId";
        const blob = new Blob([headers], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "refunds_template.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!isAdmin) {
        return (
            <Card>
                <CardHeader title="Доступ запрещен" />
                <CardContent>
                    <Typography>У вас нет прав для просмотра этой страницы.</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            <ToastNotification toast={toast} onClose={handleClose} />

            <Card>
                <CardHeader title="Загрузка возвратов из файла" />
                <CardContent>
                    <Typography paragraph>
                        Выберите CSV-файл для загрузки возвратов. Файл должен содержать следующие столбцы в указанном порядке:
                    </Typography>
                    <Typography component="div">
                        <ul>
                            <li><b>TerminalId</b> (число, обязательно) - ID терминала. Пример: <code>1</code></li>
                            <li><b>Info</b> (текст) - Дополнительная информация. Пример: <code>RRN: XXX</code></li>
                            <li><b>Text</b> (текст) - Текст чека. Пример: <code>Топливо АИ</code></li>
                            <li><b>DateTime</b> (дата и время, обязательно) - Дата и время операции в формате ISO. Пример: <code>2024-01-15T14:30:00</code></li>
                            <li><b>Sum</b> (число, обязательно) - Сумма возврата. Пример: <code>1500.50</code></li>
                            <li><b>ExternalId</b> (текст, обязательно) - Внешний уникальный идентификатор. Пример: <code>EXT-12345</code></li>
                        </ul>
                    </Typography>
                    <Typography paragraph>
                        Поле <b>ExternalId</b> является обязательным. <b>Код возврата (RefundCode)</b> генерируется автоматически на сервере.
                    </Typography>
                    <Typography paragraph sx={{ backgroundColor: '#f5f5f5', padding: 2, borderRadius: 1 }}>
                        <b>Пример строки CSV:</b><br />
                        <code>1,RRN: XXX,Топливо АИ,2024-01-15T14:30:00,1500.50,EXT-12345</code>
                    </Typography>
                    <Box mt={2}>
                        <Button
                            variant="contained"
                            component="label"
                        >
                            Выбрать файл
                            <input
                                type="file"
                                hidden
                                accept=".csv"
                                onChange={handleFileChange}
                            />
                        </Button>
                        {file && <Typography sx={{ ml: 2, display: 'inline' }}>{file.name}</Typography>}
                    </Box>
                    <Box mt={2}>
                        <Button variant="contained" color="primary" onClick={handleUpload} disabled={!file}>
                            Загрузить
                        </Button>
                    </Box>
                    <Box mt={3}>
                        <Link component="button" variant="body2" onClick={handleDownloadTemplate}>
                            Скачать шаблон CSV-файла
                        </Link>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};
